import { Chip as MuiChip, ChipProps as MuiChipProps } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  applyOpacity,
  componentTokens,
  darkComponentTokens,
} from '@peritasai/ui-shared';
import React from 'react';

/**
 * PeriVerse Themed Chip Component
 *
 * A customized chip component that follows the PeriVerse design system.
 * Extends Material-UI Chip with additional styling and variant options using semantic tokens.
 */

// Helper function to get chip tokens based on theme mode
const getChipTokens = (theme: any) => {
  return theme.palette.mode === 'dark' ? darkComponentTokens : componentTokens;
};

// Styled chip with custom styling using semantic tokens
const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) => !['size'].includes(prop as string),
})<ChipProps>(({ theme, color = 'default', variant = 'filled' }) => {
  const tokens = getChipTokens(theme);
  const chipTokens = tokens.components.chip.default;

  // Base styles - matching Figma design specs
  const baseStyles = {
    borderRadius: '9999px', // Pill shape - fully rounded
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem', // 14px - from Figma button/medium typography
    fontWeight: theme.typography.fontWeightMedium, // 500 - from Figma
    // Helvetica Neue's tall descent makes label glyphs sit ~1px high (same issue the
    // theme's MuiButton override fixes): lineHeight 1 + 1px-asymmetric vertical padding
    // nudges them to optical center within the chip's fixed height.
    lineHeight: 1,
    letterSpacing: '0.025rem', // from Figma
    textTransform: 'capitalize', // from Figma
    // Padding from Figma: 0.25 top/bottom (4px), 1 left/right (8px)
    // Override MUI's default padding
    padding: '4px 8px',
    '& .MuiChip-label': {
      padding: '0 6px', // Additional label padding to match Figma spacing
    },
  };

  // Filled variant styles
  if (variant === 'filled') {
    return {
      ...baseStyles,
      // Default color
      ...(color === 'default' && {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.primary,
        '&:hover': {
          backgroundColor: theme.palette.grey[300],
        },
        '&:focus-visible': {
          backgroundColor: chipTokens.focusFill,
          outline: `2px solid ${chipTokens.focusFill}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Primary color
      ...(color === 'primary' && {
        backgroundColor: tokens.primary.baseColor,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor:
            'dark' in tokens.primary
              ? tokens.primary.dark
              : theme.palette.primary.dark,
        },
        '&:focus-visible': {
          backgroundColor:
            'dark' in tokens.primary
              ? tokens.primary.dark
              : theme.palette.primary.dark,
          outline: `2px solid ${tokens.primary.states.focusVisible.color}`,
          outlineOffset: '2px',
        },
        '&.MuiChip-selected': {
          backgroundColor: tokens.primary.states.selected.color,
          color: theme.palette.primary.contrastText,
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Secondary color
      ...(color === 'secondary' && {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        '&:hover': {
          backgroundColor: tokens.secondary.states.hover,
        },
        '&:focus-visible': {
          backgroundColor: tokens.secondary.states.focus,
          outline: `2px solid ${tokens.secondary.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Error color
      ...(color === 'error' && {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': {
          backgroundColor: tokens.error.states.hover,
        },
        '&:focus-visible': {
          backgroundColor: tokens.error.states.hover,
          outline: `2px solid ${tokens.error.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Warning color
      ...(color === 'warning' && {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        '&:hover': {
          backgroundColor: tokens.warning.states.hover,
        },
        '&:focus-visible': {
          backgroundColor: tokens.warning.states.hover,
          outline: `2px solid ${tokens.warning.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Info color
      ...(color === 'info' && {
        backgroundColor: theme.palette.info.main,
        color: theme.palette.info.contrastText,
        '&:hover': {
          backgroundColor: tokens.info.states.hover,
        },
        '&:focus-visible': {
          backgroundColor: tokens.info.states.hover,
          outline: `2px solid ${tokens.info.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Success color
      ...(color === 'success' && {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        '&:hover': {
          backgroundColor: tokens.success.states.hover,
        },
        '&:focus-visible': {
          backgroundColor: tokens.success.states.hover,
          outline: `2px solid ${tokens.success.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Delete icon styles
      '& .MuiChip-deleteIcon': {
        color: chipTokens.closeFill,
        fontSize: '18px',
        '&:hover': {
          color: chipTokens.hoverFill,
        },
      },
    };
  }

  // Outlined variant styles
  if (variant === 'outlined') {
    return {
      ...baseStyles,
      backgroundColor: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      // Default color
      ...(color === 'default' && {
        borderColor: theme.palette.primary.dark, // chipTokens.enabledBorder,
        color: theme.palette.primary.dark, // theme.palette.text.primary,
        '&:hover': {
          backgroundColor: applyOpacity(
            theme.palette.grey[200],
            tokens.primary.states.hover.opacity
          ),
          borderColor: chipTokens.enabledBorder,
        },
        '&:focus-visible': {
          backgroundColor: applyOpacity(
            chipTokens.focusFill,
            tokens.primary.states.focus.opacity
          ),
          borderColor: chipTokens.focusFill,
          outline: `2px solid ${chipTokens.focusFill}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Primary color
      ...(color === 'primary' && {
        borderColor: tokens.primary.baseColor,
        color: tokens.primary.baseColor,
        // Override MUI's default hover styles with higher specificity
        '&:hover:not(.MuiChip-selected)': {
          backgroundColor: applyOpacity(
            tokens.primary.states.hover.color,
            tokens.primary.states.hover.opacity
          ),
          // color: tokens.primary.baseColor, // Keep text teal, not white
          // borderColor: tokens.primary.baseColor, // Keep border as primary/main
          // '& .MuiChip-label': {
          //   color: tokens.primary.baseColor, // Ensure label text stays teal
          // },
        },
        '&:focus-visible': {
          backgroundColor: applyOpacity(
            tokens.primary.states.focus.color,
            tokens.primary.states.focus.opacity
          ),
          borderColor: tokens.primary.states.outlinedBorder,
          outline: `2px solid ${tokens.primary.states.focusVisible.color}`,
          outlineOffset: '2px',
        },
        '&.MuiChip-selected': {
          backgroundColor: applyOpacity(tokens.primary.baseColor, 0.2),
          color: tokens.primary.baseColor,
          borderColor: tokens.primary.baseColor,
          '& .MuiChip-label': {
            color: tokens.primary.baseColor,
          },
          '&:hover': {
            backgroundColor: applyOpacity(tokens.primary.baseColor, 0.28),
            color: tokens.primary.baseColor,
            borderColor: tokens.primary.baseColor,
            '& .MuiChip-label': {
              color: tokens.primary.baseColor,
            },
          },
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Secondary color
      ...(color === 'secondary' && {
        borderColor: tokens.secondary.states.outlinedBorder,
        color: theme.palette.secondary.main,
        '&:hover': {
          backgroundColor: applyOpacity(
            tokens.secondary.states.hover,
            tokens.primary.states.hover.opacity
          ),
          color: theme.palette.secondary.main,
          borderColor: tokens.secondary.states.outlinedBorder,
        },
        '&:focus-visible': {
          backgroundColor: applyOpacity(
            tokens.secondary.states.focus,
            tokens.primary.states.focus.opacity
          ),
          borderColor: tokens.secondary.states.outlinedBorder,
          outline: `2px solid ${tokens.secondary.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Error color
      ...(color === 'error' && {
        borderColor: tokens.error.states.outlinedBorder,
        color: theme.palette.error.main,
        '&:hover': {
          backgroundColor: applyOpacity(
            tokens.error.states.hover,
            tokens.primary.states.hover.opacity
          ),
          color: theme.palette.error.main,
          borderColor: tokens.error.states.outlinedBorder,
        },
        '&:focus-visible': {
          backgroundColor: applyOpacity(
            tokens.error.states.focusVisible,
            tokens.primary.states.focus.opacity
          ),
          borderColor: tokens.error.states.outlinedBorder,
          outline: `2px solid ${tokens.error.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Warning color
      ...(color === 'warning' && {
        borderColor: tokens.warning.states.outlinedBorder,
        color: theme.palette.warning.main,
        '&:hover': {
          backgroundColor: applyOpacity(
            tokens.warning.states.hover,
            tokens.primary.states.hover.opacity
          ),
          color: theme.palette.warning.main,
          borderColor: tokens.warning.states.outlinedBorder,
        },
        '&:focus-visible': {
          backgroundColor: applyOpacity(
            tokens.warning.states.focusVisible,
            tokens.primary.states.focus.opacity
          ),
          borderColor: tokens.warning.states.outlinedBorder,
          outline: `2px solid ${tokens.warning.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Info color
      ...(color === 'info' && {
        borderColor: tokens.info.states.outlinedBorder,
        color: theme.palette.info.main,
        '&:hover': {
          backgroundColor: applyOpacity(
            tokens.info.states.hover,
            tokens.primary.states.hover.opacity
          ),
          color: theme.palette.info.main,
          borderColor: tokens.info.states.outlinedBorder,
        },
        '&:focus-visible': {
          backgroundColor: applyOpacity(
            tokens.info.states.focusVisible,
            tokens.primary.states.focus.opacity
          ),
          borderColor: tokens.info.states.outlinedBorder,
          outline: `2px solid ${tokens.info.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Success color
      ...(color === 'success' && {
        borderColor: tokens.success.states.outlinedBorder,
        color: theme.palette.success.main,
        '&:hover': {
          backgroundColor: applyOpacity(
            tokens.success.states.hover,
            tokens.primary.states.hover.opacity
          ),
          color: theme.palette.success.main,
          borderColor: tokens.success.states.outlinedBorder,
        },
        '&:focus-visible': {
          backgroundColor: applyOpacity(
            tokens.success.states.focusVisible,
            tokens.primary.states.focus.opacity
          ),
          borderColor: tokens.success.states.outlinedBorder,
          outline: `2px solid ${tokens.success.states.focusVisible}`,
          outlineOffset: '2px',
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
      }),
      // Delete icon styles
      '& .MuiChip-deleteIcon': {
        color: chipTokens.closeFill,
        fontSize: '18px',
        '&:hover': {
          color: chipTokens.hoverFill,
        },
      },
    };
  }

  return baseStyles;
});

export interface ChipProps
  extends Omit<MuiChipProps, 'color' | 'variant' | 'children'> {
  /**
   * The variant of the chip
   * @default 'filled'
   */
  variant?: 'filled' | 'outlined';
  /**
   * The color of the chip
   * @default 'default'
   */
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'info'
    | 'success';
  /**
   * The size of the chip
   * @default 'medium'
   */
  size?: 'small' | 'medium';
  /**
   * The label content of the chip
   */
  label?: React.ReactNode;
  /**
   * If true, the chip will be disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * If true, the chip will be clickable and will show hover effects
   * @default false
   */
  clickable?: boolean;
  /**
   * If true, the chip will appear selected
   * @default false
   */
  selected?: boolean;
  /**
   * Callback fired when the delete icon is clicked. If set, the delete icon will be shown.
   */
  onDelete?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Avatar element to display at the start of the chip
   */
  avatar?: React.ReactElement;
  /**
   * Icon element to display at the start of the chip
   */
  icon?: React.ReactElement;
  /**
   * The content of the chip (alternative to label prop)
   */
  children?: React.ReactNode;
}

/**
 * PeriVerse Chip Component
 *
 * A themed chip component that provides consistent styling across the application.
 * Supports all Material-UI Chip props with additional PeriVerse-specific styling using semantic tokens.
 *
 * @example
 * ```tsx
 * <Chip label="Default Chip" />
 *
 * <Chip label="Primary Chip" color="primary" variant="filled" />
 *
 * <Chip
 *   label="Outlined Chip"
 *   color="secondary"
 *   variant="outlined"
 *   onDelete={() => console.log('Deleted')}
 * />
 *
 * <Chip
 *   label="Small Chip"
 *   size="small"
 *   color="error"
 *   avatar={<Avatar>OP</Avatar>}
 * />
 *
 * <Chip
 *   label="Success Chip"
 *   color="success"
 *   variant="outlined"
 *   icon={<CheckIcon />}
 * />
 * ```
 */
export const Chip: React.FC<ChipProps> = ({
  variant = 'filled',
  color = 'default',
  size = 'medium',
  label,
  disabled = false,
  clickable = false,
  selected = false,
  onDelete,
  avatar,
  icon,
  children,
  ...props
}) => {
  const theme = useTheme();
  const tokens = getChipTokens(theme);

  return (
    <StyledChip
      variant={variant}
      color={color}
      size={size}
      label={label || children}
      disabled={disabled}
      clickable={clickable}
      // Don't pass selected to MUI Chip - it doesn't support this prop
      onDelete={onDelete}
      avatar={avatar}
      icon={icon}
      className={selected ? 'MuiChip-selected' : undefined}
      sx={{
        ...(selected &&
          variant === 'outlined' &&
          color === 'primary' && {
            backgroundColor: tokens.primary.states.selected.color,
            color: theme.palette.primary.contrastText,
            borderColor: tokens.primary.baseColor,
            '& .MuiChip-label': {
              color: theme.palette.primary.contrastText,
            },
            '&:hover': {
              backgroundColor: tokens.primary.states.selected.color,
              color: theme.palette.primary.contrastText,
              borderColor: tokens.primary.baseColor,
              '& .MuiChip-label': {
                color: theme.palette.primary.contrastText,
              },
            },
          }),
        ...(selected &&
          variant === 'filled' &&
          color === 'primary' && {
            backgroundColor: tokens.primary.states.selected.color,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: tokens.primary.states.selected.color,
            },
          }),
        ...props.sx,
      }}
      {...props}
    />
  );
};

export default Chip;
